import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`terms-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="font-heading text-2xl text-white mb-3">{title}</h2>
    <div className="text-white/75 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 marker:text-[#FF007F]">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="Terms & Conditions — Universal Performing Arts (UPA)"
        description="Universal Performing Arts (UPA) Terms and Conditions: enrolment, fees, notice of withdrawal, attendance, health and safety, conduct, photography, liability and more."
        canonical="https://www.upaschool.com/terms-and-conditions"
      />
      <Navbar />

      <main className="max-w-3xl mx-auto px-6 lg:px-0 py-12 lg:py-20" data-testid="terms-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF007F] transition-colors text-sm mb-8"
          data-testid="terms-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <header className="mb-10">
          <p className="text-[#FF007F] text-sm uppercase tracking-widest mb-3">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Terms &amp; Conditions
          </h1>
          <p className="text-white/60 text-base">
            Universal Performing Arts (UPA) — please read these terms carefully before enrolling.
          </p>
        </header>

        <Section title="About UPA">
          <p>
            Universal Performing Arts (UPA) is committed to providing high-quality performing arts training in a welcoming, supportive and inspiring environment. Our aim is to help every student build confidence, develop new skills, make lasting friendships and enjoy positive experiences through the arts.
          </p>
          <p>
            By enrolling a student in any UPA class, workshop, production, event or activity, parents and guardians agree to the following terms and conditions.
          </p>
        </Section>

        <Section title="Enrolment and Trial Sessions">
          <Bullets
            items={[
              'Complimentary trial sessions may be offered at the discretion of the UPA Directors.',
              'Participation in a trial session does not guarantee a place on a course or programme.',
              'All trial places and enrolments are subject to availability.',
            ]}
          />
        </Section>

        <Section title="Fees and Membership">
          <Bullets
            items={[
              'UPA reserves the right to review and amend tuition fees, membership charges and other associated costs.',
              'A minimum of 30 days’ notice will be provided before any fee changes take effect.',
              'Continued attendance after a fee change constitutes acceptance of the revised fees.',
            ]}
          />
        </Section>

        <Section title="Notice of Withdrawal">
          <Bullets
            items={[
              'Should you wish to withdraw a student from UPA, a minimum of six weeks’ written notice is required.',
              <>
                Notice must be submitted by email to{' '}
                <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">
                  theteam@upaschool.com
                </a>
                .
              </>,
              'Fees remain payable throughout the notice period.',
            ]}
          />
        </Section>

        <Section title="Attendance and Absences">
          <Bullets
            items={[
              'If a student is unable to attend due to illness, holidays, personal choice, government restrictions or any other circumstance outside UPA’s control, refunds, credits or replacement sessions cannot normally be provided.',
              'Parents and guardians should notify UPA of all absences through the Parent Portal.',
              'In cases where a student becomes medically unable to continue attending, UPA may, at its discretion, waive any remaining notice period upon receipt of appropriate evidence.',
            ]}
          />
        </Section>

        <Section title="Health, Safety and Medical Information">
          <Bullets
            items={[
              'Students participate in all UPA activities at their own risk.',
              'Parents and guardians must provide accurate medical information and disclose any injuries, conditions or additional needs that may affect participation.',
              'Emergency contact details must be kept up to date at all times through the Parent Portal.',
              'Any medication brought to UPA must be clearly labelled. Unless otherwise agreed in writing, students are responsible for administering their own medication.',
            ]}
          />
        </Section>

        <Section title="Behaviour and Conduct">
          <Bullets
            items={[
              'UPA expects all students, parents, guardians, staff, teachers and visitors to treat one another with kindness, courtesy and respect.',
              'Bullying, intimidation, discrimination, abusive language, aggressive behaviour or any form of harassment will not be tolerated.',
              'Students are expected to follow the UPA Student Code of Conduct at all times.',
              'Repeated breaches of expected behaviour standards may result in disciplinary action, including temporary suspension or permanent removal from UPA.',
              'UPA reserves the right to ask a student to leave a class, rehearsal or event if their behaviour compromises the safety, wellbeing or learning experience of others.',
            ]}
          />
        </Section>

        <Section title="Uniform and Dress Requirements">
          <Bullets
            items={[
              'Students should attend classes wearing appropriate clothing and footwear suitable for the activities being undertaken.',
              'Official UPA uniform must be worn in accordance with the school’s requirements.',
            ]}
          />
        </Section>

        <Section title="Collection of Students">
          <Bullets
            items={[
              'Parents and guardians are responsible for ensuring students are collected promptly at the end of each session.',
              'UPA accepts responsibility for students only during their scheduled class or activity times.',
            ]}
          />
        </Section>

        <Section title="Smoking, Vaping, Alcohol and Substances">
          <p>
            Smoking, vaping, alcohol consumption and the use, possession or influence of illegal substances are strictly prohibited at any venue used by UPA.
          </p>
        </Section>

        <Section title="Photography and Media Consent">
          <Bullets
            items={[
              'By completing enrolment, parents and guardians grant permission for UPA to use photographs, video recordings and other media featuring the student for promotional, educational, archival and marketing purposes.',
              'Any concerns regarding media usage should be communicated to UPA in writing.',
            ]}
          />
        </Section>

        <Section title="Personal Property">
          <Bullets
            items={[
              'Students are responsible for their own belongings.',
              'UPA cannot accept liability for lost, stolen or damaged personal items.',
            ]}
          />
        </Section>

        <Section title="Liability">
          <Bullets
            items={[
              'UPA will take all reasonable steps to provide a safe environment for students.',
              'Except in cases involving death or personal injury resulting from negligence, UPA’s liability shall be limited to the total fees paid by the parent or guardian.',
              'UPA’s responsibility does not extend beyond the duration of scheduled classes, rehearsals, performances or events.',
            ]}
          />
        </Section>

        <Section title="Cancellation and Termination">
          <Bullets
            items={[
              'This agreement may be terminated immediately by either party if there is a serious breach of these terms and conditions.',
              'UPA reserves the right to suspend or terminate membership where behaviour, conduct or non-compliance significantly impacts the academy, its staff or its students.',
            ]}
          />
        </Section>

        <Section title="Changes to Terms">
          <Bullets
            items={[
              'UPA reserves the right to amend these terms and conditions when necessary.',
              'Any significant changes will be communicated to parents and guardians in advance where reasonably possible.',
            ]}
          />
        </Section>

        <Section title="Legal Information">
          <p>
            If any part of these terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
          </p>
        </Section>

        <div className="border-t border-[#222] pt-8 mt-12 text-sm text-white/40">
          <p>
            Questions? Email us at{' '}
            <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">
              theteam@upaschool.com
            </a>
            .
          </p>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
